/**
 * Hook for automatic WebSocket reconnection on visibility change,
 * network recovery, and periodic retry when disconnected.
 * Handles token refresh by delegating to initializeConnection which
 * fetches a fresh Clerk token on each attempt.
 */

import {
  isWebSocketConnected,
  resetReconnectAttempts,
} from '@saaga/shared/store/middleware/websocket.middleware.ts'

import { logger } from '@saaga/shared/utils/logger.util.ts'
import { WS_AUTO_RECONNECT_INTERVAL_MS } from '@saaga/shared/utils/websocket-reconnect.util.ts'
import { useCallback, useEffect, useRef } from 'react'

interface UseWebsocketAutoReconnectParams {
  initializeConnection: () => Promise<void>
}

export const useWebsocketAutoReconnect = ({
  initializeConnection,
}: UseWebsocketAutoReconnectParams): void => {
  const reconnectIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const attemptReconnect = useCallback(
    (source: string) => {
      if (isWebSocketConnected()) return

      logger.info(`[WebSocket] Auto-reconnect triggered by ${source}`)
      resetReconnectAttempts()
      initializeConnection()
    },
    [initializeConnection]
  )

  const clearPeriodicReconnect = useCallback(() => {
    if (!reconnectIntervalRef.current) return
    clearInterval(reconnectIntervalRef.current)
    reconnectIntervalRef.current = null
  }, [])

  const startPeriodicReconnect = useCallback(() => {
    clearPeriodicReconnect()
    reconnectIntervalRef.current = setInterval(() => {
      attemptReconnect('periodic-retry')
    }, WS_AUTO_RECONNECT_INTERVAL_MS)
  }, [clearPeriodicReconnect, attemptReconnect])

  useEffect(() => {
    const handleVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible') return
      attemptReconnect('visibility-change')
    }

    const handleOnline = (): void => {
      attemptReconnect('network-online')
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('online', handleOnline)
    startPeriodicReconnect()

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('online', handleOnline)
      clearPeriodicReconnect()
    }
  }, [attemptReconnect, startPeriodicReconnect, clearPeriodicReconnect])
}
